import { ReactNode, useMemo, useState } from "react";
import { Group, Pagination, Table, Text } from "@mantine/core";

interface PaginatedTableProps {
  head: ReactNode;
  rows: ReactNode[];
  pageSize?: number;
  emptyMessage?: string;
}

export function PaginatedTable({
  head,
  rows,
  pageSize = 20,
  emptyMessage = "No data.",
}: PaginatedTableProps) {
  const [page, setPage] = useState(1);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);

  const slice = useMemo(() => rows.slice(start, end), [rows, start, end]);

  if (!rows.length) {
    return (
      <Text c="dimmed" size="sm">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <>
      <Table striped highlightOnHover withTableBorder withColumnBorders={false}>
        <Table.Thead>{head}</Table.Thead>
        <Table.Tbody>{slice}</Table.Tbody>
      </Table>
      {total > pageSize ? (
        <Group justify="space-between" mt="xs">
          <Text size="xs" c="dimmed">
            {start + 1}–{end} of {total}
          </Text>
          <Pagination
            size="sm"
            total={totalPages}
            value={currentPage}
            onChange={setPage}
          />
        </Group>
      ) : null}
    </>
  );
}
